import * as React from 'react';
import { createContext, useContext, useRef, useCallback, useMemo } from 'react';
import { view, createViewConfig } from '@sail/engine';
import { CollectionProvider, useListBox, useListCollection, useReactAriaTreeState } from '@sail/react-aria';
import { FauxTreeItem, FauxTreeItemTransforms } from './FauxTreeItem';
import { FauxTreeGroup, FauxTreeGroupTransforms } from './FauxTreeGroup';
import { FauxTreeSelectAllItem } from './FauxTreeSelectAllItem';
import { FauxTreeProvider } from '../hooks/useFauxTreeState';
import { ListBoxEmptyState } from './ListBox';
export const FauxTreeConfig = createViewConfig({
    name: 'FauxTree',
    props: {},
    defaults: {
        selectionMode: 'single',
        selectionAllEnabled: true,
        defaultExpandedKeys: 'all',
        expandable: false,
        groupCounterEnabled: true,
        groupSelectionAllEnabled: true
    }
});
export const FauxTreeContext = /*#__PURE__*/ createContext({});
const useListBoxState = (props)=>{
    const { state: inheritedState, collection: inheritedCollection, options: inheritedOptions } = useContext(FauxTreeContext);
    // Track whether we're using inherited state or not so we can't violate the
    // rules of hooks by switching between the two.
    const isUsingInheritedState = useRef(Boolean(inheritedState && inheritedCollection));
    if (isUsingInheritedState.current) {
        return {
            state: inheritedState,
            collection: inheritedCollection,
            options: inheritedOptions
        };
    }
    // Create collection and based on the incoming props
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const collection = useListCollection({
        ...props,
        itemTransforms: FauxTreeItemTransforms,
        groupTransforms: FauxTreeGroupTransforms
    });
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const state = useReactAriaTreeState({
        getItems: collection.getItems,
        ...props
    });
    return {
        collection,
        state,
        options: {}
    };
};
export const DO_NOT_USE_FauxTree = FauxTreeConfig.createGenericView(({ data, item, group, children, label, onSelectionChange, selectionMode, selectionAllEnabled, defaultSelectedKeys, selectedKeys, disabledKeys, emptyState = /*#__PURE__*/ React.createElement(ListBoxEmptyState, null), expandedKeys, defaultExpandedKeys, onExpandedChange, expandable, messages, groupCounterEnabled, groupSelectionAllEnabled, ...props })=>{
    const { collection, state, options } = useListBoxState({
        data,
        item,
        group,
        children,
        selectionMode,
        onSelectionChange,
        defaultSelectedKeys,
        selectedKeys,
        disabledKeys,
        expandedKeys,
        defaultExpandedKeys,
        onExpandedChange,
        expandable
    });
    const filter = useCallback((item)=>{
        return Boolean((item === null || item === void 0 ? void 0 : item.key) && state.collection.getItem(item.key));
    }, [
        state
    ]);
    const isEmpty = useMemo(()=>collection.getItems().filter(filter).length === 0, [
        collection,
        filter
    ]);
    const isMultiple = state.selectionManager.selectionMode === 'multiple';
    const ref = React.useRef(null);
    const { listBoxProps } = useListBox({
        label,
        shouldFocusWrap: true,
        ...options
    }, state, ref);
    return /*#__PURE__*/ React.createElement(FauxTreeProvider, {
        value: children
    }, /*#__PURE__*/ React.createElement(CollectionProvider, {
        collection: collection,
        state: state
    }, /*#__PURE__*/ React.createElement(view.div, {
        ref: ref,
        ...props,
        ...listBoxProps
    }, !isEmpty && isMultiple && selectionAllEnabled ? /*#__PURE__*/ React.createElement(FauxTreeSelectAllItem, {
        selectedText: messages === null || messages === void 0 ? void 0 : messages.selectedText,
        ...options.selectAllRef ? {
            selectAllRef: options.selectAllRef
        } : {}
    }) : null, collection.render((props)=>/*#__PURE__*/ React.createElement(FauxTreeItem, {
            key: props.id,
            ...props
        }, props.children || props.textValue), (props)=>/*#__PURE__*/ React.createElement(FauxTreeGroup, {
            key: props.id,
            counterEnabled: groupCounterEnabled,
            groupSelectionAllEnabled: groupSelectionAllEnabled,
            ...props
        }), filter), isEmpty && emptyState)));
}, {
    css: {
        stack: 'y',
        gap: 'space.0',
        backgroundColor: 'surface',
        borderRadius: 'medium',
        keyline: 'border',
        keylineWidth: 'small',
        padding: 'xsmall',
        focusRing: 'none'
    }
});

//# sourceMappingURL=FauxTree.js.map